import { useMemo } from 'react';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { SCREENS } from '../../constants/tracking/Screens';
import { useSortedProductInventory } from '../../hooks/inventory/useSortedProductInventory';
import { useOwnedBundleForProduct } from '../../hooks/inventory/useProductInventory';
import { getNavItemForProductLine } from '../../../utils/routingUtils';
import { isValidProductLine } from '../../utils/typeguards';
import { withErrorBoundary } from '../error-boundary/withErrorBoundary';
import { CustomError } from '../error-boundary/CustomErrors';
import { SingleSkeletonLoader } from '../SingleSkeletonLoader';
import { UnlockAIContentLayout } from './UnlockAIContentLayout';
import { useHighestProductLevelFromInventory } from '../../hooks/inventory/useHighestProductLevel';
import { useAppSelector } from '../../../reducers';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { BillingNameTitle } from './BillingNameTitle';
import { jsx as _jsx } from "react/jsx-runtime";
const UnlockAIContentBase = () => {
  var _firstProduct$product;
  const {
    sortedProductInventory,
    loading: sortedProductInventoryLoading,
    error: sortedProductInventoryError
  } = useSortedProductInventory();
  const {
    highestProductLevel,
    loading: highestProductLevelLoading,
    error: highestProductLevelError
  } = useHighestProductLevelFromInventory();
  const isPreSeats = !useAppSelector(isUngatedForSeatsSelector);
  const firstProduct = sortedProductInventory[0];
  const billingNameKey = (_firstProduct$product = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productApiName) !== null && _firstProduct$product !== void 0 ? _firstProduct$product : '';

  // Get bundle type for the first product if it exists
  const {
    bundleType,
    loading: bundleTypeLoading,
    error: bundleTypeError
  } = useOwnedBundleForProduct({
    apiName: firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productApiName
  });
  const isLoading = sortedProductInventoryLoading || bundleTypeLoading || highestProductLevelLoading;
  const subscreen = useMemo(() => {
    const isBundle = !!(firstProduct !== null && firstProduct !== void 0 && firstProduct.bundle);
    if (isBundle) {
      if (bundleType === 'MARKETING_PLUS_SUITE') {
        return SCREENS.MARKETING_PLUS;
      }
      if (bundleType === 'CRM_SUITE') {
        return SCREENS.SUITE;
      }
    }
    const productLine = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productLine;
    if (!isValidProductLine(productLine)) {
      return SCREENS.SUITE;
    }
    return getNavItemForProductLine(productLine);
  }, [firstProduct, bundleType]);
  const title = useMemo(() => {
    if (highestProductLevel === 'STARTER') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.pages.hubspot-credits.unlockAI.title.withStarter"
      });
    }
    if (isPreSeats) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.pages.hubspot-credits.unlockAI.title.withHubSpot"
      });
    }
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "pricingPage.pages.hubspot-credits.unlockAI.title.withBillingName",
      options: {
        billingName: /*#__PURE__*/_jsx(BillingNameTitle, {
          billingNameKey: billingNameKey
        })
      }
    });
  }, [highestProductLevel, isPreSeats, billingNameKey]);
  if (sortedProductInventoryError) throw new CustomError('Failed getting sorted product inventory', {
    error: sortedProductInventoryError
  });
  if (bundleTypeError) throw new CustomError('Failed getting bundle type from product', {
    error: bundleTypeError
  });
  if (highestProductLevelError) throw new CustomError('Failed getting highest product level from inventory', {
    error: highestProductLevelError
  });
  if (isLoading) {
    return /*#__PURE__*/_jsx(SingleSkeletonLoader, {
      height: 1000,
      width: "100%"
    });
  }
  return /*#__PURE__*/_jsx(UnlockAIContentLayout, {
    subscreen: subscreen,
    title: title
  });
};
export const UnlockAIContent = withErrorBoundary(UnlockAIContentBase, {
  fallback: null,
  errorBoundaryName: 'UnlockAIContent'
});